import { useEffect, useState } from "react";
import FleetcomService from "../../../services/resource/fleetcomService";
import Chart from 'react-apexcharts';
import ResumoCard from "../../bp/dashboards/ResumoCard";
import { Loading } from "../../../componentes/loading/loading";
import DropDown from "../../../componentes/dropdown/dropdown";
import InputForm from "../../../componentes/input/input";
import { intervaloDatas } from "../../../utils/options";
import { formatLocalDate, getAnteontem, getComecoAno, getDataSeteDiasAntesFormatado, getHojeFormatado, getMesAtual, getMesPassado, getOntemFormatado, ultimoDiaMesPassado } from "../../../utils/formatDate";
import SplitButton from "../../../componentes/button-split/SplitButton";
import { exportaComQualidade } from "../../../utils/exportarDashboardEmPDF";
import { captureElementToImage } from "../../../utils/captureElementToImage";
import { copyElementToClipboard } from "../../../utils/copyElementToClipboard";
import { formatarMoedaBrasileira } from "../../../utils/formatarMoedaBrasileira";
import Button from "../../../componentes/button/button";
import { KPICard } from "../../../componentes/chart/KPICard";

// ─── Paleta ───────────────────────────────────────────────────────────────────
const CHART_COLORS = ["#0EA5E9", "#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EA580C"];

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard2({ label, value, sub, valorDescricao, icon, color, isLoading, progresso }) {
    return (
        <div className="surface-card p-5 flex flex-col gap-1 overflow-hidden relative">
            {/* barra de cor no topo */}
            <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl" style={{ background: color }} />

            <div className="flex items-start justify-between">
                <span className="text-xl">{icon}</span>
                {progresso !== undefined && (
                    <span className="text-[10px] font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                        {progresso?.toFixed(1)}%
                    </span>
                )}
            </div>

            {isLoading ? (
                <div className="h-8 w-3/4 bg-muted animate-pulse rounded-lg mt-1" />
            ) : (
                <span className="text-2xl font-display font-bold tracking-tight text-foreground mt-1 leading-none">{value}</span>
            )}

            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>

            {sub && (
                <span className="text-xs text-muted-foreground mt-0.5">
                    {sub}{valorDescricao && <strong className="text-foreground ml-1">{valorDescricao}</strong>}
                </span>
            )}
        </div>
    );
}

// ─── Chart Card (padrão BPRelatorio) ─────────────────────────────────────────
function ChartCard({ title, children, loading, accent }) {
    return (
        <div className="surface-panel">
            {/* header */}
            <div className="flex items-center gap-2 px-3 py-3.5 border-b border-border bg-muted">
                {accent && <div className="w-0.5 h-4 rounded-full flex-shrink-0" style={{ background: accent }} />}
                <h3 className="text-sm font-display font-bold text-foreground m-0">{title}</h3>
            </div>
            <div className="p-3">
                {loading ? <div className="flex items-center justify-center h-52"><Loading /></div> : children}
            </div>
        </div>
    );
}

// ─── Bar Chart ────────────────────────────────────────────────────────────────
function BarChart({ title, height, data, horizontal = false, loading, onSelect, dataLabels = false, columnWidth = 40, currency = true, labelSeries, accent }) {
    const safeData = {
        labels: Array.isArray(data?.labels) ? data.labels : [],
        series: Array.isArray(data?.series) ? data.series : [],
    };

    return (
        <ChartCard title={title} loading={loading} accent={accent}>
            <Chart
                type="bar"
                height={height ?? 300}
                series={[{ name: labelSeries ?? "Valor", data: safeData.series }]}
                options={{
                    chart: {
                        toolbar: { show: false },
                        animations: { enabled: true, easing: "easeinout", speed: 800 },
                        events: {
                            dataPointSelection: (_e, _ctx, cfg) => {
                                const label = safeData.labels[cfg.dataPointIndex];
                                onSelect?.(label);
                            },
                        },
                    },
                    plotOptions: { bar: { horizontal, borderRadius: 4, columnWidth, distributed: true } },
                    colors: CHART_COLORS,
                    xaxis: { categories: safeData.labels, labels: { style: { colors: "#64748b", fontSize: "11px" } }, axisBorder: { show: false }, axisTicks: { show: false } },
                    yaxis: { labels: { style: { colors: "#64748b" } } },
                    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
                    tooltip: {
                        theme: "light",
                        y: { formatter: (v) => currency ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : v },
                    },
                    dataLabels: {
                        enabled: dataLabels,
                        style: { fontSize: "9px", fontWeight: 600 },
                        formatter: (v) => currency ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : v,
                    },
                    legend: { show: false },
                    states: { active: { filter: { type: "darken", value: 0.85 } } },
                }}
            />
        </ChartCard>
    );
}

// ─── Donut / Pie Chart ────────────────────────────────────────────────────────
function DonutChart({ title, data, loading, onSelect, dataLabels, showLegend, type = "donut", accent }) {
    const safeData = {
        labels: Array.isArray(data?.labels) ? data.labels : [],
        series: Array.isArray(data?.series) ? data.series : [],
    };

    return (
        <ChartCard title={title} loading={loading} accent={accent}>
            <Chart
                type={type}
                height={300}
                series={safeData.series}
                options={{
                    labels: safeData.labels,
                    colors: CHART_COLORS,
                    chart: {
                        toolbar: { show: false },
                        events: {
                            dataPointSelection: (_e, _ctx, cfg) => {
                                const label = safeData.labels[cfg.dataPointIndex];
                                onSelect?.(label);
                            },
                        },
                    },
                    tooltip: {
                        theme: "light",
                        y: { formatter: (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
                    },
                    stroke: { show: false, width: 0 },
                    legend: { show: showLegend, position: "bottom", labels: { colors: "#64748b" } },
                    dataLabels: { enabled: dataLabels, style: { fontSize: "10px" } },
                    plotOptions: {
                        pie: {
                            donut: {
                                size: "62%",
                                labels: {
                                    show: type === "donut",
                                    total: {
                                        show: true, label: "Total", color: "#94a3b8", fontSize: "12px",
                                        formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                                            .toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
                                    },
                                },
                            },
                        },
                    },
                }}
            />
        </ChartCard>
    );
}

// ─── Divider de seção ─────────────────────────────────────────────────────────
function SectionDivider({ label }) {
    return (
        <div className="flex items-center gap-3 my-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
            <div className="flex-1 h-px bg-border" />
        </div>
    );
}

// ─── Componente Principal ─────────────────────────────────────────────────────
function RelatorioNotasVeiculos() {
    const service = new FleetcomService();
    const FileDownload = require("js-file-download");

    const [relatorio, setRelatorio] = useState({
        gastoPorFilial: [], gastoPorCentroCusto: [], gastoPorVeiculo: [],
        gastoPorFornecedor: [], quantidadeNotasPorFilial: [], gastoPorGrupoVeicular: [],
        gastoTotal: 0, veiculoComMaiorGasto: 0, quantidadeNotas: 0, veiculoComMaiorGastoPlaca: "",
    });
    const [loading, setLoading] = useState(true);
    const [loadingExportacao, setLoadingExportacao] = useState(false);
    const [filtro, setFiltro] = useState(null);
    const [dataInicial, setDataInicial] = useState(getMesAtual());
    const [dataFinal, setDataFinal] = useState(getHojeFormatado());
    const [dataSelecionada, setDataSelecionada] = useState(7);

    useEffect(() => { fetchRelatorio(); }, []);

    const fetchRelatorio = async () => {
        setLoading(true);
        try {
            const response = await service.dashboardNotasManutencao(dataInicial, dataFinal);
            setRelatorio(response.data);
        } finally {
            setLoading(false);
        }
    };

    const mudarPeriodoDeDatas = (e) => {
        const valor = e.target.value;
        setDataSelecionada(valor);
        if (valor === 14) { setFiltro(true); return; }
        setFiltro(false);
        if (valor === 7) { setDataInicial(getMesAtual()); setDataFinal(getHojeFormatado()); }
        else if (valor === 0) { setDataInicial(getHojeFormatado()); setDataFinal(getHojeFormatado()); }
        else if (valor === 1) { setDataInicial(getOntemFormatado()); setDataFinal(getOntemFormatado()); }
        else if (valor === 2) { setDataInicial(getAnteontem()); setDataFinal(getAnteontem()); }
        else if (valor === 30) { setDataInicial(getMesPassado()); setDataFinal(ultimoDiaMesPassado()); }
        else if (valor === 365) { setDataInicial(getComecoAno()); setDataFinal(getHojeFormatado()); }
        else if (valor === 700) { setDataInicial("2025-01-01"); setDataFinal("2025-12-31"); }
        else { setDataInicial(getDataSeteDiasAntesFormatado(valor)); setDataFinal(getHojeFormatado()); }
    };

    // ── Dados dos gráficos ───────────────────────────────────────────────────
    const gastoPorFilialChart        = { labels: relatorio.gastoPorFilial.map(i => i.label),              series: relatorio.gastoPorFilial.map(i => Number(i.total)) };
    const gastoPorFornecedorChart    = { labels: relatorio.gastoPorFornecedor.map(i => i.label),          series: relatorio.gastoPorFornecedor.map(i => Number(i.total)) };
    const gastoPorCentroCustoChart   = { labels: relatorio.gastoPorCentroCusto.map(i => i.label),        series: relatorio.gastoPorCentroCusto.map(i => Number(i.total)) };
    const gastoPorVeiculoChart       = { labels: relatorio.gastoPorVeiculo.map(i => i.placa),             series: relatorio.gastoPorVeiculo.map(i => Number(i.total)) };
    const gastoPorGrupoVeicularChart = { labels: relatorio.gastoPorGrupoVeicular.map(i => i.label),      series: relatorio.gastoPorGrupoVeicular.map(i => Number(i.total)) };
    const notasPorFilialChart        = { labels: relatorio.quantidadeNotasPorFilial.map(i => i.label),   series: relatorio.quantidadeNotasPorFilial.map(i => Number(i.total)) };

    return (
        <div className="min-h-screen bg-background font-sans text-foreground">

            {/* ── Topbar ─────────────────────────────────────────────────── */}
            <header className="sticky top-0 z-50 bg-card border-b border-border shadow-xs">
                <div className="flex items-center justify-between px-6 h-14">

                    {/* Logo + título */}
                    <div className="flex items-center gap-3.5">
                        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-primary-foreground text-xs font-display font-extrabold tracking-tight shrink-0">
                            NF
                        </div>
                        <div>
                            <p className="text-sm font-display font-bold text-foreground leading-tight">Notas de Manutenção</p>
                            <p className="text-[11px] text-muted-foreground">Relatório de gastos por veículo</p>
                        </div>
                    </div>

                    {/* Ações */}
                    <div className="flex items-center gap-2">
                        <SplitButton
                            label="Exportar Excel"
                            color="green"
                            textColor="white"
                            icon="fas fa-file-excel"
                            loading={loadingExportacao}
                            onClick={() => {
                                setLoadingExportacao(true);
                                service.exportarExcel(dataInicial, dataFinal)
                                    .then((res) => FileDownload(res.data, `Notas de Manutencao de ${dataInicial} à ${dataFinal}.xlsx`))
                                    .finally(() => setLoadingExportacao(false));
                            }}
                            items={[
                                {
                                    label: "Exportar PDF", icon: "fas fa-file-pdf",
                                    onClick: () => exportaComQualidade(
                                        ["relatorio-de-manutencao-container"],
                                        "Relatório de Notas de Manutenção",
                                        `${formatLocalDate(dataInicial)} a ${formatLocalDate(dataFinal)}`
                                    ),
                                },
                                {
                                    label: "Baixar Imagem", icon: "fas fa-camera",
                                    onClick: () => captureElementToImage("relatorio-de-manutencao-container"),
                                },
                                {
                                    label: "Copiar Imagem", icon: "fas fa-clipboard",
                                    onClick: () => copyElementToClipboard("relatorio-de-manutencao-container"),
                                },
                            ]}
                        />
                    </div>
                </div>
            </header>

            <main className="px-5 py-3 space-y-4 max-w-screen-2xl mx-auto">

                {/* ── Filtros ─────────────────────────────────────────────── */}
                <div className="bg-card border border-border rounded-2xl px-5 py-4 shadow-card">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="flex flex-wrap items-end gap-2">
                            <DropDown
                                optional
                                label="Período"
                                options={intervaloDatas}
                                optionLabel="label"
                                optionValue="value"
                                onChange={mudarPeriodoDeDatas}
                                value={dataSelecionada}
                                className="w-56"
                            />
                            {filtro && (
                                <div className="flex gap-2 fade-in">
                                    <InputForm
                                        label="Data Inicial"
                                        type="date"
                                        onChange={(e) => setDataInicial(e.target.value)}
                                        value={dataInicial}
                                        className="h-10 px-3 py-2 border border-input rounded-xl focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm"
                                    />
                                    <InputForm
                                        label="Data Final"
                                        type="date"
                                        onChange={(e) => setDataFinal(e.target.value)}
                                        value={dataFinal}
                                        className="h-10 px-3 py-2 border border-input rounded-xl focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm"
                                    />
                                </div>
                            )}
                        </div>
                        <Button
                            label="Pesquisar"
                            variant="indigo"
                            iconLeft="fa fa-search"
                            onClick={fetchRelatorio}
                            loading={loading}
                        />
                    </div>
                </div>

                {/* ── Conteúdo exportável ─────────────────────────────────── */}
                <div id="relatorio-de-manutencao-container" className="space-y-4">

                    {/* KPIs */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <KPICard
                            label="Total de Notas"
                            value={relatorio.quantidadeNotas}
                            icon="📄"
                            color="#6366F1"
                            isLoading={loading}
                            progresso={100}
                            sub="Total de notas cadastradas"
                        />
                        <KPICard
                            label="Total Gasto"
                            value={`R$ ${formatarMoedaBrasileira(relatorio.gastoTotal)}`}
                            icon="💰"
                            color="#0EA5E9"
                            isLoading={loading}
                            sub="Média por nota"
                            valorDescricao={`R$ ${formatarMoedaBrasileira(relatorio.gastoTotal / relatorio.quantidadeNotas)}`}
                        />
                        <KPICard
                            label="Veículo com Maior Gasto"
                            value={relatorio.veiculoComMaiorGastoPlaca}
                            icon="🚛"
                            color="#F59E0B"
                            isLoading={loading}
                            progresso={(relatorio.veiculoComMaiorGasto / relatorio.gastoTotal) * 100}
                            sub="Valor gasto no período"
                            valorDescricao={`R$ ${formatarMoedaBrasileira(relatorio.veiculoComMaiorGasto)}`}
                        />
                    </div>

                    {/* Gráficos — linha 1 */}
                    <SectionDivider label="Distribuição de gastos" />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <BarChart
                            title="Gasto por Filial"
                            data={gastoPorFilialChart}
                            loading={loading}
                            accent="#0EA5E9"
                        />
                        <DonutChart
                            title="Distribuição por Fornecedor"
                            data={gastoPorFornecedorChart}
                            dataLabels
                            loading={loading}
                            showLegend={false}
                            accent="#6366F1"
                        />
                    </div>

                    {/* Gráfico veículo — full width */}
                    <SectionDivider label="Gasto por veículo" />
                    <BarChart
                        title="Gasto por Veículo"
                        data={gastoPorVeiculoChart}
                        horizontal
                        dataLabels
                        loading={loading}
                        height={600}
                        accent="#10B981"
                    />

                    {/* Gráficos — linha 2 */}
                    <SectionDivider label="Análise complementar" />
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                        <BarChart
                            title="Gasto por Centro de Custo"
                            data={gastoPorCentroCustoChart}
                            horizontal
                            loading={loading}
                            accent="#8B5CF6"
                        />
                        <DonutChart
                            title="Gasto por Grupo Veicular"
                            data={gastoPorGrupoVeicularChart}
                            dataLabels
                            type="pie"
                            loading={loading}
                            showLegend
                            accent="#F59E0B"
                        />
                        <BarChart
                            title="Quantidade de Notas por Filial"
                            data={notasPorFilialChart}
                            currency={false}
                            dataLabels
                            loading={loading}
                            labelSeries="Quantidade"
                            accent="#EF4444"
                        />
                    </div>
                </div>
            </main>
        </div>
    );
}

export default RelatorioNotasVeiculos;